<template>
    <div ref="container">
        <div>
            <pc-capsule
                v-bind:id="`${inputPropertyId}.headersCapsule`"
                v-model:shown="showHeadersWrapper"
                v-bind:title="label"
                has-body>
                <pc-loader v-bind:loading="isLoading">
                    <div v-if="hasParsingError">
                        <div class="alert alert-danger">
                            <h6>{{ parsingErrorMessage }}</h6>
                        </div>
                        <pc-button
                            primary
                            v-on:click="clearHeaders()">
                            {{ clearHeadersLabel }}
                        </pc-button>
                    </div>
                    <div v-else>
                        <pc-table
                            v-bind:columns="columns"
                            v-bind:items="rows"
                            hide-search
                            key-prop="Index"
                            v-bind:empty-text="emptyListText">
                            <!-- requires referencing the row from this.rows for v-model to work -->
                            <template v-slot:Key="row">
                                <pc-input
                                    v-bind:model-value="rows[row.Index].Key"
                                    v-bind:data-test-role="`testRole.${inputPropertyId}.key${row.Index}`"
                                    v-bind:validator="headerValidator"
                                    v-bind:alt-label="headerNameInputLabel"
                                    v-bind:disabled="disabled"
                                    v-on:update:model-value="updateHeaderName(row.Index, $event)"
                                    v-on:validated="onValidated(`key${row.Index}`, $event)" />
                            </template>
                            <template v-slot:Value="row">
                                <div class="d-flex align-items-center">
                                    <div class="flex-grow-1">
                                        <pc-input
                                            v-bind:model-value="rows[row.Index].Value"
                                            v-bind:data-test-role="`testRole.${inputPropertyId}.value${row.Index}`"
                                            v-bind:validator="headerValidator"
                                            v-bind:disabled="disabled"
                                            type="text"
                                            v-bind:alt-label="headerValueInputLabel"
                                            v-on:update:model-value="updateRowValue(row.Index, $event)"
                                            v-on:validated="onValidated(`Value${row.Index}`, $event)" />
                                    </div>
                                    <div v-if="!disabled">
                                        <img
                                            src="~Images/icons/trash.svg"
                                            class="capsule-icon ms-1 me-1"
                                            v-on:click="removeHeader(row.Index)" />
                                    </div>
                                </div>
                            </template>
                        </pc-table>
                        <pc-button
                            v-if="!disabled"
                            v-bind:id="`${inputPropertyId}.addHeaderButton`"
                            v-on:click="addHeader">
                            {{ addHeaderLabel }}
                        </pc-button>
                    </div>
                </pc-loader>
            </pc-capsule>
            <div
                v-if="!showHeaders && hasAnyErrors"
                class="alert alert-danger">
                <h6>{{ invalidHeadersMessage }}</h6>
            </div>
        </div>
    </div>
</template>
<script>
    import base from "Components/_inputBase.vue";
    import pcInput from "Components/pc-input.vue";
    import pcCapsule from "Components/pc-capsule.vue";
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import PcButton from "Components/pc-button.vue";
    import { areStringsEqualIgnoreCase } from "Util/util.js";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/headersEditor.i18n";

    export default {
        components: { pcInput, PcButton, pcCapsule },
        extends: base,
        props: {
            authorizationInUse: { type: Boolean, required: false, default: false },
            // Used for test roles and IDs
            inputPropertyId: { type: String, required: true, default: "" },
            validateReservedHeaders: { type: Boolean, required: false, default: false }
        },
        emits: ["plaintextRequested", "validated"],
        data: function () {
            return {
                columns: [
                    {
                        property: "Index",
                        orderable: true,
                        searchable: false,
                        visible: false
                    },
                    {
                        header: this.headerKeyColumnLabel,
                        property: "Key",
                        orderable: false,
                        searchable: false,
                        visible: true
                    },
                    {
                        header: this.headerValueColumnLabel,
                        property: "Value",
                        orderable: false,
                        searchable: false,
                        visible: true
                    }
                ],
                rows: [],
                hasParsingError: false,
                showHeaders: false,
                validationResults: {},
                hasPlaintext: false,
                plainTextValue: null,
                isLoading: false,

                reservedHeaders: ["Content-Type", "Accept"],
                authorizationHeader: "Authorization"
            };
        },
        computed: {
            showHeadersWrapper: {
                get() {
                    return this.showHeaders;
                },
                set(value) {
                    this.showHeaders = value;
                    this.onCapsuleToggled(value);
                }
            },
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            ),
            headerStringValidator() {
                return {
                    type: VALIDATOR_TYPES.regEx,
                    regEx: /^[a-zA-Z0-9 =_@.\\/-]*$/,
                    errorMsg: this.invalidHeaderValueMessage
                };
            },
            headerValidator() {
                return getValidator(this.$app, [
                    VALIDATOR_TYPES.required,
                    this.headerStringValidator,
                    {
                        type: VALIDATOR_TYPES.custom,
                        validate: this._isHeaderNotDuplicate,
                        errorMsg: this.duplicateHeadersErrorMessage,
                        skipTrimValue: true
                    },
                    {
                        type: VALIDATOR_TYPES.custom,
                        validate: this._isHeaderNotDuplicateAuthorization,
                        errorMsg: this.authorizationAlreadySetErrorMessage,
                        skipTrimValue: true
                    },
                    {
                        type: VALIDATOR_TYPES.custom,
                        validate: this._isHeaderNotReserved,
                        errorMsg: this.headerReservedErrorMessage,
                        skipTrimValue: true
                    }
                ]);
            },
            valueValidator() {
                return getValidator(this.$app, [VALIDATOR_TYPES.required, this.headerStringValidator]);
            },
            hasAnyErrors() {
                // Used to determine whether the list could not be parsed OR there are
                // invalid headers specified, without having to do each check individually.
                if (this.hasParsingError) {
                    return true;
                }
                for (const key in this.validationResults) {
                    if (this.validationResults.hasOwnProperty(key)) {
                        if (this.validationResults[key].hasError) {
                            return true;
                        }
                    }
                }
                return false;
            }
        },
        watch: {
            value(value) {
                // Check that the update isn't coming from within the headers editor
                let active = document.activeElement;
                if (active.type === "text" || active.type === "submit") {
                    // Check the parents of the element to see if it's inside the headers editor
                    let depth = 0;
                    while (active !== null && depth <= 15) {
                        if (active === this.$refs.container) {
                            return;
                        }

                        active = active.parentElement;
                        depth++;
                    }
                }

                this.validateCurrentValue(value);
            },
            authorizationInUse() {
                if (this.hasAnyErrors) {
                    this.validationHub.validate();
                }
            }
        },
        created() {
            this.validateCurrentValue(this.modelValue);
        },
        methods: {
            // Callback for when the plaintext value was retrieved successfully
            onPlaintextValueSet(value) {
                this.isLoading = false;
                this.hasPlaintext = true;
                this.plainTextValue = value;
                this.validateCurrentValue(this.plainTextValue);
            },
            // Callback for when the plaintext value could not be retrieved for any reason
            onPlaintextRequestCancelled() {
                this.isLoading = false;
                this.showHeaders = false;
            },
            delayValidate() {
                setTimeout(() => this.validationHub.validate(), 0);
            },
            validateCurrentValue(value) {
                // If we only have the obfuscated value, don't validate it
                if (this.hasPlaintext) {
                    this.hasParsingError = !this.getHeadersFromValue(value);
                    this.emitValidation();
                }
            },
            addHeader() {
                this.rows.push({
                    Key: "Key",
                    Value: "Value",
                    Index: this.rows.length
                });
                this.delayValidate();
                this.onInput();
            },
            removeHeader(index) {
                this.rows.splice(index, 1);
                for (let i = index; i < this.rows.length; i++) {
                    this.rows[i].Index--;
                }

                // Reset validation results, so errors from removed headers don't stick around
                this.validationResults = {};
                if (this.rows.length === 0) {
                    // Need to do this manually when there are no rows because validation normally comes from the rows
                    this.emitValidation();
                }
                this.delayValidate();
                this.onInput();
            },
            clearHeaders() {
                this.rows.splice(0, this.rows.length);
                this.hasParsingError = false;
                this.onInput();
            },
            getHeadersFromValue(value) {
                // Parses the header from the value property as JSON
                // returns true if successful, false if there's an error
                this.rows.splice(0, this.rows.length);

                if (value === null) {
                    // If value is null, this means there are no custom headers
                    return true;
                }

                let obj = {};
                try {
                    // Must be valid JSON, or it is not valid
                    obj = JSON.parse(value);
                } catch (ex) {
                    return false;
                }

                // JSON object must be an array
                if (!Array.isArray(obj)) {
                    return false;
                }

                // Make sure each element is a valid header entry
                // If it is, push it with the correct index
                for (let i = 0; i < obj.length; i++) {
                    const item = obj[i];
                    for (const member in item) {
                        if (!item.hasOwnProperty(member)) {
                            // can't be a prototype
                            return false;
                        }
                        // Make sure it's a known member with the expected type
                        switch (member) {
                            case "Key":
                                if (typeof item[member] !== "string") {
                                    return false;
                                }
                                break;
                            case "Value":
                                if (typeof item[member] !== "string") {
                                    return false;
                                }
                                break;
                            default:
                                return false;
                        }
                    }

                    // It's good, so add it to the list of rows
                    this.rows.push({ ...item, Index: i });
                }

                return true;
            },
            onInputHeaderName() {
                this.delayValidate();
                this.onInput();
            },
            updateRowValue(index, value) {
                this.rows[index].Value = value;
                this.onInput();
            },
            updateHeaderName(index, value) {
                this.rows[index].Key = value;
                this.onInputHeaderName();
            },
            onInput() {
                // Serialize the rows without the indices
                const rows = this.rows.map((row) => ({ Key: row.Key, Value: row.Value }));
                const value = JSON.stringify(rows);
                if (this.internalValue !== value) {
                    this.internalValue = value;
                }
            },
            onValidated(key, result) {
                const updatedResults = JSON.parse(JSON.stringify(this.validationResults));
                updatedResults[key] = result;
                this.validationResults = updatedResults;
                this.emitValidation();
            },
            onCapsuleToggled(value) {
                if (value && !this.hasPlaintext) {
                    this.isLoading = true;
                    this.$emit(
                        "plaintextRequested",
                        this.onPlaintextValueSet.bind(this),
                        this.onPlaintextRequestCancelled.bind(this)
                    );
                }
            },
            summarizeErrors() {
                const errors = [];

                if (this.hasParsingError) {
                    errors.push(this.parsingErrorMessage);
                }
                for (const key in this.validationResults) {
                    if (this.validationResults.hasOwnProperty(key)) {
                        const validationResult = this.validationResults[key];
                        if (validationResult.hasError) {
                            if (!errors.includes(validationResult.error)) {
                                errors.push(validationResult.error);
                            }
                        }
                    }
                }

                return errors;
            },
            emitValidation() {
                const errors = this.summarizeErrors();
                const hasErrors = errors.length > 0;

                this.$emit("validated", {
                    error: errors.join(", "),
                    hasError: hasErrors,
                    type: "custom"
                });
            },
            _isHeaderNotDuplicate(value) {
                const trimmed = value.trim();
                let count = 0;
                for (let i = 0; i < this.rows.length; i++) {
                    if (areStringsEqualIgnoreCase(this.rows[i].Key.trim(), trimmed)) {
                        count++;
                    }
                }

                return count < 2;
            },
            _isHeaderNotReserved(value) {
                if (!this.validateReservedHeaders) {
                    return true;
                }
                const trimmed = value.trim();
                for (let i = 0; i < this.reservedHeaders.length; i++) {
                    if (areStringsEqualIgnoreCase(this.reservedHeaders[i].trim(), trimmed)) {
                        return false;
                    }
                }

                return true;
            },
            _isHeaderNotDuplicateAuthorization(value) {
                if (!this.authorizationInUse) {
                    // If Authorization header isn't already in use, the user is free to provide their own
                    return true;
                }
                const trimmed = value.trim();
                return !areStringsEqualIgnoreCase(this.authorizationHeader, trimmed);
            }
        }
    };
</script>

<style lang="scss" scoped>
    .capsule-icon {
        border: 0;
        width: 1.5rem;
        height: 1.5rem;
        filter: invert(100%);
    }
</style>
